import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import { Chip } from "src/components/atoms/Chip";
import { Notification } from "src/components/atoms/Notification";
import { ListItem } from "src/components/molecules/ListItem";
import styles from "src/components/organisms/NotificationDropdown/styles.module.css";
import { MAX_PAGE_SIZE, MIN_PAGE } from "src/constants/size";
import { msg, Msg } from "src/i18n/Msg";
import { useGetNotifications } from "src/queries/useGetNotifications";
import { useMarkAsReadNotification } from "src/queries/useMarkAsReadNotification";
import { setNotificationIds } from "src/store/notifications/notificationsSlice";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import { Notification as NotificationType } from "src/types/api";
import { debounce } from "lodash";

const maxHeight = 426;

export const NotificationDropdown: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const toggleButtonRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const { data } = useGetNotifications({ page: MIN_PAGE, size: MAX_PAGE_SIZE });
  const notifications = data?.data?.data || [];
  const { markReadMutate } = useMarkAsReadNotification();
  const markedReadIds = useRef<Set<string>>(new Set());

  const markRead = (ids: string[]) => {
    const newIds = ids.filter((id) => !markedReadIds.current.has(id));
    if (newIds.length > 0) {
      markReadMutate({ notification_ids: newIds });
      newIds.forEach((id) => markedReadIds.current.add(id));
      newIds.forEach((id) => dispatch(setNotificationIds(id)));
    }
  };

  const handleNotificationClick = (id: string) => {
    dispatch(setNotificationIds(id));
    if (!markedReadIds.current.has(id)) {
      markReadMutate({ notification_ids: [id] });
      markedReadIds.current.add(id);
    }
  };

  const onRowsRendered = ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    const visibleIds = notifications
      .slice(startIndex, stopIndex + 1)
      .map((n: NotificationType) => n.id);
    markRead(visibleIds);
  };

  const handleToggle = () => {
    setIsOpen((prev) => {
      if (!prev) {
        markedReadIds.current = new Set();
      }
      return !prev;
    });
  };

  const debouncedToggle = debounce(handleToggle, 100);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 100,
    })
  );

  useEffect(() => {
    cache.current.clearAll();
  }, [notifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);
  const listHeight = Math.min(notifications.length * 60, maxHeight);

  return (
    <div className={styles.notificationWrapper}>
      <div ref={toggleButtonRef}>
        <Notification
          handleClick={debouncedToggle}
          count={data?.data?.unread_count}
        />
      </div>
      {isOpen && (
        <div className={styles.notification} ref={dropdownRef}>
          <h3>
            <Msg id="components.routes.pages.Notifications.title" />
          </h3>
          {notifications.length > 0 ? (
            <div className={styles.list} style={{ height: listHeight }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    width={width}
                    height={height}
                    rowCount={notifications.length}
                    rowHeight={cache.current.rowHeight}
                    deferredMeasurementCache={cache.current}
                    rowRenderer={({ index, key, parent, style }) => {
                      const notification = notifications[index];
                      return (
                        <CellMeasurer
                          key={key}
                          cache={cache.current}
                          parent={parent}
                          columnIndex={0}
                          rowIndex={index}
                        >
                          <div style={{ ...style, whiteSpace: "normal" }}>
                            <ListItem
                              title={notification.long_title || notification.title}
                              info={notification.long_text || notification.text}
                              notification
                              id={notification.id}
                              link={notification.object_link}
                              win={notification.is_plus}
                              img={notification.notification_image}
                              isWithdraw={notification.is_plus !== null}
                              onClick={() =>
                                handleNotificationClick(notification.id)
                              }
                            />
                          </div>
                        </CellMeasurer>
                      );
                    }}
                    onRowsRendered={onRowsRendered}
                    overscanRowCount={3}
                  />
                )}
              </AutoSizer>
            </div>
          ) : (
            <div className={styles.empty}>
              <Chip
                text={msg(intl, {
                  id: "components.organisms.NotificationDropdown.noData",
                })}
              />
            </div>
          )}
        </div>
      )}
      <ToastContainer />
    </div>
  );
};
